import { useEffect, useRef, useState } from "react";
import {
  Search, X, RotateCcw, Sliders, ChevronDown, Calendar as CalIcon, Coins,
} from "lucide-react";
import { formatDoctorName } from "@/lib/doctorName";
import { PIPELINE_STATES } from "./pipelineHelpers";

// =====================================================================
// Tx Plan Center FilterBar — horizontal, Shopify-style
// =====================================================================
// Replaces the left-side FilterSidebar so the kanban / Work Queue gets
// the full horizontal real estate. Layout is one row of compact controls:
//
//   [🔍 Search ............] [Doctor ▼] [Stage ▼] [Advanced ▼] · N matched · Reset
//
// "Advanced" is a popover that holds the less-frequent filters (plan
// value range, created-between dates) so they don't push the primary
// controls off the row on narrow viewports.
// =====================================================================

export default function FilterBar({
  filters, onChange, doctors, planCount, hasAny, onReset,
}) {
  // Patient-search input is debounced so we don't refetch on every keystroke.
  const [search, setSearch] = useState(filters.patient_search || "");
  useEffect(() => { setSearch(filters.patient_search || ""); }, [filters.patient_search]);
  useEffect(() => {
    const t = setTimeout(() => {
      if ((filters.patient_search || "") !== search) {
        onChange({ ...filters, patient_search: search });
      }
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const advancedActive =
    !!filters.min_value || !!filters.max_value ||
    !!filters.date_from || !!filters.date_to;

  return (
    <div
      data-testid="tx-plan-center-filters"
      data-tutorial-id="tx-plan-center-filters"
      className="rounded-lg border border-slate-200 bg-white px-2.5 py-2 flex flex-wrap items-center gap-2"
    >
      {/* Patient search */}
      <div className="relative flex-1 min-w-[220px] max-w-[340px]">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" strokeWidth={2} />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search patient name, # or plan code"
          className="w-full pl-7 pr-7 py-1.5 text-[13px] rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 focus:border-teal-500"
        />
        {search ? (
          <button
            type="button"
            onClick={() => setSearch("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
          >
            <X className="w-3 h-3" strokeWidth={2.5} />
          </button>
        ) : null}
      </div>

      {/* Doctor */}
      <CompactSelect
        label="Doctor"
        value={filters.doctor_id || ""}
        onChange={(v) => onChange({ ...filters, doctor_id: v })}
        options={[{ value: "", label: "All doctors" }, ...(doctors || []).map((d) => ({
          value: d.id, label: formatDoctorName(d),
        }))]}
      />

      {/* Stage */}
      <CompactSelect
        label="Stage"
        value={filters.pipeline_state || ""}
        onChange={(v) => onChange({ ...filters, pipeline_state: v })}
        options={[{ value: "", label: "All stages" }, ...PIPELINE_STATES.map((s) => ({
          value: s.key, label: s.label,
        }))]}
      />

      {/* Advanced popover — value + date range */}
      <AdvancedPopover
        active={advancedActive}
        filters={filters}
        onChange={onChange}
      />

      <div className="flex-1" />

      {/* Result count + reset */}
      <div className="flex items-center gap-2 text-[11px] text-slate-500">
        {planCount === null || planCount === undefined ? (
          <span>—</span>
        ) : (
          <span className="tabular-nums">
            {planCount.toLocaleString()} match{planCount === 1 ? "" : "es"}
          </span>
        )}
        {hasAny ? (
          <button
            type="button"
            onClick={onReset}
            className="inline-flex items-center gap-1 px-2 py-1 rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <RotateCcw className="w-3 h-3" strokeWidth={2.25} />
            Reset
          </button>
        ) : null}
      </div>

      {/* Active filter chips (rendered below the row when any are set
          beyond search). Search is already visible in the input so no
          chip needed. */}
      {(filters.doctor_id || filters.pipeline_state || advancedActive) ? (
        <div className="basis-full flex flex-wrap gap-1 pt-1.5 border-t border-slate-100 mt-0.5">
          {filters.doctor_id ? (
            <FilterChip
              label={`Doctor: ${formatDoctorName((doctors || []).find((d) => d.id === filters.doctor_id), "—")}`}
              onClear={() => onChange({ ...filters, doctor_id: "" })}
            />
          ) : null}
          {filters.pipeline_state ? (
            <FilterChip
              label={`Stage: ${PIPELINE_STATES.find((s) => s.key === filters.pipeline_state)?.label || filters.pipeline_state}`}
              onClear={() => onChange({ ...filters, pipeline_state: "" })}
            />
          ) : null}
          {filters.min_value || filters.max_value ? (
            <FilterChip
              label={`Value: ${filters.min_value ? "₱" + filters.min_value : ""}${filters.min_value || filters.max_value ? "–" : ""}${filters.max_value ? "₱" + filters.max_value : ""}`}
              onClear={() => onChange({ ...filters, min_value: "", max_value: "" })}
            />
          ) : null}
          {filters.date_from || filters.date_to ? (
            <FilterChip
              label={`Created: ${filters.date_from || "…"} → ${filters.date_to || "…"}`}
              onClear={() => onChange({ ...filters, date_from: "", date_to: "" })}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Compact native <select> styled to look like a Shopify filter pill.
// ---------------------------------------------------------------------
function CompactSelect({ label, value, onChange, options }) {
  const active = !!value;
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={[
          "appearance-none pl-2.5 pr-7 py-1.5 text-[12px] rounded-md border bg-white",
          active
            ? "border-teal-500 text-teal-800 ring-1 ring-teal-100"
            : "border-slate-300 text-slate-700 hover:border-slate-400",
          "focus:outline-none focus:ring-1 focus:ring-teal-500",
        ].join(" ")}
      >
        {options.map((o) => (
          <option key={o.value || "__all"} value={o.value}>{label}: {o.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-1.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" strokeWidth={2.25} />
    </div>
  );
}

// ---------------------------------------------------------------------
// Advanced popover — plan value + date range. Click-outside closes.
// ---------------------------------------------------------------------
function AdvancedPopover({ active, filters, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          "inline-flex items-center gap-1.5 px-2.5 py-1.5 text-[12px] rounded-md border bg-white",
          active
            ? "border-teal-500 text-teal-800 ring-1 ring-teal-100"
            : "border-slate-300 text-slate-700 hover:border-slate-400",
        ].join(" ")}
      >
        <Sliders className="w-3.5 h-3.5" strokeWidth={2.25} />
        Advanced
        {active ? <span className="ml-0.5 inline-block w-1.5 h-1.5 rounded-full bg-teal-500" /> : null}
        <ChevronDown className="w-3.5 h-3.5 text-slate-400" strokeWidth={2.25} />
      </button>

      {open ? (
        <div className="absolute right-0 top-full mt-1 z-30 w-[280px] rounded-lg border border-slate-200 bg-white shadow-lg p-3 space-y-3">
          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1">
              <Coins className="w-3 h-3" strokeWidth={2.25} />
              Plan value (₱)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                inputMode="numeric"
                value={filters.min_value || ""}
                onChange={(e) => onChange({ ...filters, min_value: e.target.value })}
                placeholder="Min"
                className="w-full py-1.5 px-2 text-[13px] rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 tabular-nums"
              />
              <span className="text-slate-400 text-[13px]">–</span>
              <input
                type="number"
                inputMode="numeric"
                value={filters.max_value || ""}
                onChange={(e) => onChange({ ...filters, max_value: e.target.value })}
                placeholder="Max"
                className="w-full py-1.5 px-2 text-[13px] rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-teal-500 tabular-nums"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1">
              <CalIcon className="w-3 h-3" strokeWidth={2.25} />
              Created between
            </label>
            <div className="space-y-1.5">
              <input
                type="date"
                value={filters.date_from || ""}
                onChange={(e) => onChange({ ...filters, date_from: e.target.value })}
                className="w-full py-1.5 px-2 text-[13px] rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
              <input
                type="date"
                value={filters.date_to || ""}
                onChange={(e) => onChange({ ...filters, date_to: e.target.value })}
                className="w-full py-1.5 px-2 text-[13px] rounded-md border border-slate-300 bg-white focus:outline-none focus:ring-1 focus:ring-teal-500"
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------
// Removable filter chip
// ---------------------------------------------------------------------
function FilterChip({ label, onClear }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-teal-50 text-teal-800 text-[11px] ring-1 ring-teal-200">
      {label}
      <button
        type="button"
        onClick={onClear}
        className="p-0.5 rounded hover:bg-teal-100"
        aria-label={`Clear ${label}`}
      >
        <X className="w-3 h-3" strokeWidth={2.5} />
      </button>
    </span>
  );
}
